import { CustomButton, Modal, PdfViewer, SvgCross } from '@ui-kit'
import { useEffect, useState } from 'react'
import {
  BUTTONS,
  COMM_TEMPLATE,
  ENTITY_NAME,
  PERMISSION_MODULES,
} from '../../utils/constants'
import {
  API_END_POINT,
  axiosInstance,
  fetchHcpFormPdfUrl,
  hideLoader,
  showLoader,
} from '@app-store'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import './FaxDocumentModal.scss'
import { DYNAMIC_QUEUE_MESSAGES } from '../../constants/dynamicQueue'
import { permissionObject } from '../../utils/helperFunctions'
import { ShortcutKeys } from '../../constants/form.constants'
import { WEB_PREVIEW } from '../../constants/intakeQue.constants'
import { isReadOnlyUser, isSuperAdmin } from '../../utils/permission'
import DocumentPopout from '../send-manual-communication/DocumentPopout'

export const FaxDocumentModal = ({
  isOpen,
  closeModal,
  faxId,
  faxName,
  handleReviewButtonClick,
  communicationId,
  users_id,
  programs_id,
  entity_name,
  queueType,
}: {
  isOpen: boolean
  closeModal: () => void
  faxId: number
  faxName: string
  handleReviewButtonClick: (
    id: number | string,
    communication_id: number | string
  ) => Promise<void>
  communicationId: number | string
  users_id: string
  programs_id: string
  entity_name?: string
  queueType?: string
}) => {
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions.programPermissions
  )
  const authUser = useAppSelector((state: RootState) => state?.authUser)
  const [loading, setLoading] = useState(true)
  const [faxUrl, setFaxUrl] = useState('')

  const dispatch = useAppDispatch()

  const selectedTenantId = useAppSelector(
    (state: RootState) => state.authUser.selectedClientTenant?.id
  )

  const getFaxDetails = async () => {
    let finalFaxUrl = null

    try {
      setLoading(true)
      dispatch(showLoader())
      if (faxId) {
        if (entity_name === ENTITY_NAME.HCP_PORTAL_ENTITY) {
          const response = await fetchHcpFormPdfUrl(faxName ?? '')
          finalFaxUrl = response?.data?.url
        } else if (!faxName.startsWith('L')) {
          const faxUrlResponse = await axiosInstance.get(
            `${API_END_POINT.DELIVERY}/v1/communications/get-presigned-url/${faxId}`,
            {
              headers: {
                'X-TenantId': selectedTenantId,
              },
            }
          )
          finalFaxUrl = faxUrlResponse.data?.url
        } else {
          const faxUrlResponse = await axiosInstance.get(
            `${API_END_POINT.DELIVERY}/v1/communications/get-presigned-url/bulk-print?recordIds=${faxId}`,
            {
              headers: {
                'X-TenantId': selectedTenantId,
              },
            }
          )
          finalFaxUrl = faxUrlResponse.data
        }
        setFaxUrl(finalFaxUrl)
      }
    } catch (e) {
      console.log(e)
    } finally {
      dispatch(hideLoader())
      setLoading(false)
    }

    return finalFaxUrl
  }

  const getDocumentTypeLabel = () => {
    if (entity_name === ENTITY_NAME.HCP_PORTAL_ENTITY) {
      return WEB_PREVIEW.ENROLLMENT_ID
    }
    if (faxName.startsWith('M')) {
      return DYNAMIC_QUEUE_MESSAGES.MAIL_ID
    }
    if (!faxName.startsWith('L')) {
      return DYNAMIC_QUEUE_MESSAGES.FAX_ID
    }
    return DYNAMIC_QUEUE_MESSAGES.LETTER_ID
  }

  useEffect(() => {
    getFaxDetails()
  }, [faxId])

  return (
    !loading && (
      <Modal
        isOpen={isOpen}
        closeModal={closeModal}
        bodyCssClassName="min-h-[40rem] max-h-[80rem] fax-document-modal"
        showCloseButton={false}
        isDragable
      >
        <div className="flex flex-col h-full">
          <div className={`flex justify-between items-center pl-5 pr-1 py-4`}>
            <div className="font-primary font-bold text-base">{`${getDocumentTypeLabel()} : ${faxName}`}</div>
            <div className="flex items-center">
              {(isSuperAdmin() ||
                !(
                  users_id !== authUser.user?.id?.toString() ||
                  !permissionObject.Queue_Review_Manage_Intake(
                    programPermissions,
                    PERMISSION_MODULES.INTAKE_TRIAGE,
                    'Manage',
                    programs_id
                  )
                ) ||
                (users_id === authUser.user?.id?.toString() &&
                  queueType === COMM_TEMPLATE.LETTER_LOWECASE)) && (
                <>
                  <CustomButton
                    dataQaId={'review-button'}
                    type="button"
                    color="primary"
                    onClick={() => {
                      handleReviewButtonClick(faxId, communicationId)
                    }}
                    className="h-10 "
                    isFilled={true}
                    disabled={isReadOnlyUser()}
                  >
                    <div className="flex items-center justify-center w-full gap-1 svg-path-stroke-white path">
                      <span>{BUTTONS.EDIT}</span>
                    </div>
                  </CustomButton>
                  <div className="h-6 border-l border-border-300 mx-2" />
                </>
              )}

              <DocumentPopout
                source={faxUrl}
                generateUrl={getFaxDetails}
                className="mr-3"
              />
              <div
                className="cursor-pointer p-1"
                onClick={closeModal}
                data-qa-id={'close-document-modal'}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (
                    e?.key === ShortcutKeys?.Enter ||
                    e?.key === ShortcutKeys?.Space
                  ) {
                    closeModal()
                  }
                }}
              >
                <span className="text-black">
                  <SvgCross />
                </span>
              </div>
            </div>
          </div>
          <div className="border w-[100%] border-border-100 rounded h-[75vh] relative">
            <PdfViewer pdfUrl={faxUrl} height="h-full" showLoader={true} />
          </div>
        </div>
      </Modal>
    )
  )
}
